<template>
  <div class="app-view">
    <!-- Top bar -->
    <header class="app-view__header">
      <button class="back-btn" @click="goBack">← Dashboard</button>
      <div class="header__title">
        <span class="header__icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <path d="M7 7h10M7 12h10M7 17h6"/>
          </svg>
        </span>
        <div>
          <span class="header__name">Einbürgerungsantrag</span>
          <span class="header__sub">Antrag auf Verleihung der deutschen Staatsangehörigkeit · Berliner Senatsverwaltung</span>
        </div>
      </div>
      <div class="header__badge">FormIQ 2 · AutoFill</div>
    </header>

    <!-- Showcase banner -->
    <ShowcaseBanner />

    <!-- AutoFill notification bar (shown after autofill) -->
    <div v-if="autoFillDone && currentStep > 0" class="autofill-bar">
      <span>🔵</span>
      <strong>AutoFill aktiv:</strong> Blau hervorgehobene Felder wurden automatisch aus Ihrem Dokument ausgelesen. Alle Felder können bearbeitet werden.
    </div>

    <!-- Main layout: form + sidebar -->
    <div class="app-view__body">

      <!-- Left: Form area -->
      <div class="form-area" style="position: relative;">

        <!-- Step 0: Upload Documents -->
        <AutoFillUpload
          v-if="currentStep === 0"
          :client-id="resolvedClientId"
          @autofill-done="onAutoFillDone"
          @skip="onSkip"
        />

        <!-- Steps 1-3: Native Vue form components -->
        <component
          v-else
          :is="currentStepComponent"
          v-model="formData"
          :showErrors="showErrors"
        />

        <!-- AutoFill toast (shown briefly after injection) -->
        <div v-if="showAutoFillToast" class="autofill-toast">
          ✦ AutoFill aktiv — <strong>{{ autoFilledCount }} Felder</strong> automatisch ausgefüllt
        </div>
      </div>

      <!-- Right: Sidebar navigation -->
      <aside class="sidebar">
        <div class="sidebar__header">
          <span class="sidebar__icon">📋</span>
          <span class="sidebar__title">Formularnavigation</span>
        </div>
        <nav class="sidebar__nav">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="sidebar__item"
            :class="{
              'sidebar__item--active': index === currentStep,
              'sidebar__item--done': index < currentStep,
              'sidebar__item--locked': index > currentStep,
            }"
            @click="goToStep(index)"
          >
            <div class="sidebar__indicator">
              <span v-if="index < currentStep" class="indicator--done">✓</span>
              <span v-else-if="index === currentStep" class="indicator--active">{{ index + 1 }}</span>
              <span v-else class="indicator--pending">{{ index + 1 }}</span>
            </div>
            <div class="sidebar__label-wrap">
              <span class="sidebar__label">{{ step }}</span>
              <span v-if="index > 0 && autoFillDone && index === 3" class="sidebar__badge">AutoFill</span>
            </div>
          </div>
        </nav>
        <div class="sidebar__progress">
          <div class="progress-bar">
            <div class="progress-bar__fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <span class="progress-text">{{ currentStep + 1 }} von {{ steps.length }} Schritten</span>
        </div>
      </aside>
    </div>

    <!-- Navigation bar at bottom -->
    <div class="nav-bar">
      <button
        class="nav-btn nav-btn--secondary"
        :disabled="currentStep === 0"
        @click="prevStep"
      >
        ← Zurück
      </button>
      <div class="nav-bar__center">
        <span class="nav-bar__step-label">Schritt {{ currentStep + 1 }}: {{ steps[currentStep] }}</span>
      </div>
      <button
        v-if="currentStep < steps.length - 1"
        class="nav-btn nav-btn--primary"
        @click="nextStep"
      >
        Weiter →
      </button>
      <button
        v-else
        class="nav-btn nav-btn--finish"
        @click="showFinish = true"
      >
        Antrag absenden ✓
      </button>
    </div>

    <!-- Selection popover -->
    <SelectionPopover />

    <!-- Floating chat -->
    <ChatWindow form-id="form_2" :client-id="resolvedClientId" :ui-payload="currentUiPayload" />

    <!-- Finish modal -->
    <div v-if="showFinish" class="modal-overlay" @click.self="showFinish = false">
      <div class="modal">
<!--        <div class="modal__icon">🎉</div>-->
        <h2>Antrag eingereicht!</h2>
        <p>
          Dies ist eine Showcase-Demo – es wurden keine echten Daten übermittelt.
          Vielen Dank für das Erleben von FormIQ AutoFill &amp; Chat Assist!
        </p>
        <div class="modal__actions">
          <button class="nav-btn nav-btn--secondary" @click="showFinish = false">Schließen</button>
          <button class="nav-btn nav-btn--primary" @click="goBack">← Zurück zum Dashboard</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import ShowcaseBanner from '../components/ShowcaseBanner.vue';
import ChatWindow from '../components/ChatWindow.vue';
import SelectionPopover from '../components/SelectionPopover.vue';
import AutoFillUpload from '../components/AutoFillUpload.vue';
import NatStep1 from '../forms/naturalization/NatStep1.vue';
import NatStepInfo from '../forms/naturalization/NatStepInfo.vue';
import NatStep2 from '../forms/naturalization/NatStep2.vue';
import NatStep3 from '../forms/naturalization/NatStep3.vue';
import { flowAssignmentApi } from '../services/flowAssignmentApi.js';

const FLOW_KEY = 'formiq2';
const DEFAULT_CLIENT_ID = 'form2_einbuergerung';

// Demo data injected when AutoFill is active
const DEMO_AUTOFILL_DATA = {
  anrede: 'Frau',
  vorname: 'Maria',
  nachname: 'Mustermann',
  geburtsdatum: '1985-03-15',
  geburtsort: 'Istanbul',
  staatsangehoerigkeit: 'türkisch',
  email: 'maria.mustermann@example.de',
  mobil: '+49 151 23456789',
};

export default {
  name: 'ApplicationViewB',
  components: { ShowcaseBanner, ChatWindow, SelectionPopover, AutoFillUpload, NatStep1, NatStepInfo, NatStep2, NatStep3 },

  setup() {
    const router = useRouter();
    const showFinish = ref(false);
    const showErrors = ref(false);
    const autoFillDone = ref(false);
    const showAutoFillToast = ref(false);
    const autoFilledCount = ref(0);
    const resolvedClientId = ref(DEFAULT_CLIENT_ID);

    onMounted(async () => {
      try {
        const assignment = await flowAssignmentApi.getFlowAssignment(FLOW_KEY);
        if (assignment?.client_id) resolvedClientId.value = assignment.client_id;
      } catch {
        // keep the default client_id if the lookup fails
      }
    });

    const steps = [
      'Dokumente hochladen',
      'Datenschutz',
      'Antragsinformationen',
      'Persönliche Daten',
      'Zusammenfassung',
    ];

    // Step components: step 0 = AutoFillUpload, steps 1-4 = Nat components
    const stepComponents = [null, NatStep1, NatStepInfo, NatStep2, NatStep3];
    const currentStep = ref(0);
    const currentStepComponent = computed(() => stepComponents[currentStep.value]);
    const progressPercent = computed(() => Math.round((currentStep.value / (steps.length - 1)) * 100));

    // Form data store for all steps
    const formData = ref({
      // Step 1 (NatStep1 – Datenschutz)
      confirm_voraussetzungen: false,
      confirm_datenschutz: false,
      // Step 2 (NatStepInfo – Antragsinformationen / Einholen von Auskünften)
      confirm_kenntnisnahme: false,
      confirm_auskuenfte: false,
      // Step 3 (NatStep2 – Persönliche Daten)
      antragstellungsart: '',
      anrede: '',
      titel: '',
      vorname: '',
      nachname: '',
      geburtsdatum: '',
      geburtsort: '',
      staatsangehoerigkeit: '',
      email: '',
      mobil: '',
      // AutoFill tracking
      _autofilled: {},
    });

    const validateCurrentStep = () => {
      const d = formData.value;
      switch (currentStep.value) {
        case 0: return true; // Upload step — skip/done handled separately
        case 1: return d.confirm_datenschutz;
        case 2: return d.confirm_kenntnisnahme && d.confirm_auskuenfte;
        case 3:
          return d.antragstellungsart && d.anrede && d.vorname && d.nachname &&
                 d.geburtsdatum && d.geburtsort && d.staatsangehoerigkeit &&
                 d.email && d.mobil;
        default: return true;
      }
    };

    const goToStep = (index) => {
      if (index <= currentStep.value) {
        currentStep.value = index;
        showErrors.value = false;
      }
    };

    const nextStep = () => {
      if (currentStep.value === 0) {
        // On upload step, onAutoFillDone / onSkip handles navigation
        return;
      }
      if (!validateCurrentStep()) {
        showErrors.value = true;
        return;
      }
      showErrors.value = false;
      if (currentStep.value < steps.length - 1) {
        currentStep.value++;
      }
    };

    const prevStep = () => {
      showErrors.value = false;
      if (currentStep.value > 0) {
        currentStep.value--;
      }
    };

    const goBack = () => router.push('/dashboard');

    const injectFields = (data) => {
      const autofilled = {};
      const updates = {};
      Object.entries(data).forEach(([key, value]) => {
        if (value) { updates[key] = value; autofilled[key] = true; }
      });
      formData.value = { ...formData.value, ...updates, _autofilled: autofilled };
      autoFilledCount.value = Object.keys(updates).length;
      showAutoFillToast.value = true;
      setTimeout(() => { showAutoFillToast.value = false; }, 4000);
    };

    const onAutoFillDone = (extractedData) => {
      autoFillDone.value = true;
      const cvExtraction = extractedData?.document_extractions?.find(d => d.document_type === 'CV')?.extraction;
      if (cvExtraction) {
        injectFields({
          anrede: cvExtraction.salutation,
          vorname: cvExtraction.first_name,
          nachname: cvExtraction.last_name,
          geburtsdatum: cvExtraction.date_of_birth,
          geburtsort: cvExtraction.place_of_birth,
          staatsangehoerigkeit: cvExtraction.nationality,
          email: cvExtraction.email,
          mobil: cvExtraction.mobile_phone,
        });
      } else {
        // fallback to demo data when extraction yielded no structured output
        injectFields(DEMO_AUTOFILL_DATA);
      }
      currentStep.value = 1;
    };

    const onSkip = () => {
      currentStep.value = 1; // go to prerequisites
    };

    // Screen IDs matching the step index
    const screenIds = [
      'nat-dokumente-hochladen',
      'nat-datenschutz',
      'nat-antragsinformationen',
      'nat-personaldaten',
      'nat-zusammenfassung',
    ];

    const stepFields = [
      [],
      ['confirm_voraussetzungen', 'confirm_datenschutz'],
      ['confirm_kenntnisnahme', 'confirm_auskuenfte'],
      ['antragstellungsart','anrede','titel','vorname','nachname','geburtsdatum',
       'geburtsort','staatsangehoerigkeit','email','mobil'],
      [],
    ];

    const stepRequired = {
      1: ['confirm_datenschutz'],
      2: ['confirm_kenntnisnahme', 'confirm_auskuenfte'],
      3: ['antragstellungsart','anrede','vorname','nachname','geburtsdatum',
          'geburtsort','staatsangehoerigkeit','email','mobil'],
    };

    const currentUiPayload = computed(() => {
      const step   = currentStep.value;
      const d      = formData.value;
      const sid    = screenIds[step];
      const fields = stepFields[step] || [];
      const required = stepRequired[step] || [];

      const field_values = {};
      fields.forEach(k => { if (k !== '_autofilled') field_values[k] = d[k]; });

      const field_errors = {};
      if (showErrors.value) {
        required.forEach(k => {
          const v = d[k];
          if (v === '' || v === null || v === false || v === undefined) {
            field_errors[k] = 'Dieses Feld ist erforderlich.';
          }
        });
      }

      const form_progress = { current_step: step + 1, total_steps: steps.length, steps: {} };
      steps.forEach((name, i) => {
        const status = i < step ? 'completed' : i === step ? 'in_progress' : 'pending';
        form_progress.steps[screenIds[i]] = { name, status };
      });

      return { screen_id: sid, field_values, field_errors, form_progress };
    });

    return {
      steps, currentStep, currentStepComponent, progressPercent,
      formData, showErrors,
      showFinish, autoFillDone, showAutoFillToast, autoFilledCount,
      resolvedClientId,
      goToStep, nextStep, prevStep, goBack,
      onAutoFillDone, onSkip,
      currentUiPayload,
    };
  },
};
</script>

<style scoped>
.app-view {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #eef0f4;
  overflow: hidden;
}

/* Header */
.app-view__header {
  background: #ffffff;
  border-bottom: 3px solid #c0121c;
  padding: 10px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #0f172a;
  flex-shrink: 0;
  gap: 16px;
}

.back-btn {
  background: #f1f5f9;
  border: 1px solid #d0d7de;
  color: #1e293b;
  padding: 7px 14px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}
.back-btn:hover { background: #e2e8f0; }

.header__title { display: flex; align-items: center; gap: 12px; flex: 1; }
.header__icon { font-size: 22px; display: flex; align-items: center; color: #c0121c; }
.header__name { display: block; font-size: 15px; font-weight: 700; color: #1a1a1a; }
.header__sub { display: block; font-size: 11px; color: #64748b; margin-top: 2px; }

.header__badge {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #065f46;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

/* AutoFill notification bar */
.autofill-bar {
  background: #eef4fb;
  border-bottom: 1px solid #c3d9f0;
  padding: 8px 24px;
  font-size: 13px;
  color: #1d4ed8;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* Body: form + sidebar */
.app-view__body {
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  min-height: 0;
}

/* Form area */
.form-area {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  background: #fff;
  border-right: 1px solid #d0d7de;
}

/* AutoFill toast */
.autofill-toast {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: #003366;
  color: #fff;
  padding: 10px 22px;
  border-radius: 4px;
  font-size: 13px;
  z-index: 500;
  white-space: nowrap;
  box-shadow: 0 4px 20px rgba(0, 51, 102, 0.4);
  animation: slideDown 0.3s ease;
}
@keyframes slideDown {
  from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* Sidebar */
.sidebar {
  width: 220px;
  flex-shrink: 0;
  background: #f5f7fa;
  border-left: 1px solid #d0d7de;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.sidebar__header {
  padding: 16px 16px 12px;
  border-bottom: 2px solid #c0121c;
  display: flex;
  align-items: center;
  gap: 8px;
}

.sidebar__icon { font-size: 16px; }

.sidebar__title {
  font-size: 12px;
  font-weight: 700;
  color: #c0121c;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.sidebar__nav {
  flex: 1;
  padding: 8px 0;
}

.sidebar__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: default;
  border-left: 3px solid transparent;
  transition: background 0.15s, border-color 0.15s;
}

.sidebar__item--done {
  cursor: pointer;
  border-left-color: #1a7a45;
}
.sidebar__item--done:hover { background: #e8f5ee; }

.sidebar__item--active {
  background: #eef4fb;
  border-left-color: #003366;
}

.sidebar__item--locked { opacity: 0.55; }

.sidebar__indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.indicator--done {
  background: #1a7a45;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
}

.indicator--active {
  background: #003366;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  box-shadow: 0 0 0 3px rgba(0, 51, 102, 0.18);
}

.indicator--pending {
  background: #e2e8f0;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
}

.sidebar__label-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sidebar__label {
  font-size: 13px;
  color: #1a1a1a;
  font-weight: 500;
  line-height: 1.3;
}

.sidebar__item--active .sidebar__label {
  font-weight: 700;
  color: #003366;
}

.sidebar__item--done .sidebar__label {
  color: #155534;
}

.sidebar__badge {
  font-size: 10px;
  font-weight: 700;
  background: #dbeafe;
  color: #1d4ed8;
  padding: 1px 7px;
  border-radius: 3px;
  width: fit-content;
}

.sidebar__progress {
  padding: 14px 16px;
  border-top: 1px solid #d0d7de;
}

.progress-bar {
  height: 5px;
  background: #d0d7de;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 6px;
}

.progress-bar__fill {
  height: 100%;
  background: #003366;
  border-radius: 3px;
  transition: width 0.35s ease;
}

.progress-text {
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
}

/* Navigation bar */
.nav-bar {
  background: #f5f7fa;
  border-top: 2px solid #d0d7de;
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 12px;
}

.nav-bar__center {
  flex: 1;
  text-align: center;
}

.nav-bar__step-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

.nav-btn {
  padding: 10px 28px;
  border-radius: 3px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s, transform 0.1s, background 0.15s;
}
.nav-btn:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
.nav-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.nav-btn--primary { background: #003366; color: #fff; }
.nav-btn--primary:hover:not(:disabled) { background: #002244; opacity: 1; }
.nav-btn--secondary { background: #ffffff; color: #1a1a1a; border: 1px solid #adb5bd; }
.nav-btn--secondary:hover:not(:disabled) { background: #f0f4f9; opacity: 1; }
.nav-btn--finish { background: #1a7a45; color: #fff; }
.nav-btn--finish:hover:not(:disabled) { background: #155534; opacity: 1; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.modal {
  background: #fff;
  border-radius: 8px;
  padding: 40px 36px;
  max-width: 440px;
  width: 90%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  border-top: 4px solid #c0121c;
}
.modal__icon { font-size: 52px; }
.modal h2 { font-size: 22px; font-weight: 800; color: #1a1a2e; }
.modal p { font-size: 14px; color: #555; line-height: 1.6; }
.modal__actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }

/* Responsive */
@media (max-width: 700px) {
  .sidebar { display: none; }
}
</style>
